import { DateRange } from '@adamftd/x-date-pickers-pro';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { Point } from 'highcharts';
import { filter, get, isNumber } from 'lodash';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import DateRangePickerWithDefaultIcon from 'src/components/DateRangePicker';
import LineChart from 'src/components/HighChart/LineChart';
import { VesselDetailBox } from 'src/features/VesselDetail/config';
import { useQueryVesselDetailMetrics } from 'src/services/queries/useQueryVessel';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { getDatePickerValue, handleDatePickerValueChange } from 'src/utils/helpers/dayjs';

const VesselMetrics = () => {
  const { id } = useParams();
  const [dateRange, setDateRange] = useState({
    from_date: dayjs().subtract(1, 'y').format(FORMAT_DATE_TIME.yyyy_mm_dd) as string | null,
    to_date: dayjs().format(FORMAT_DATE_TIME.yyyy_mm_dd) as string | null,
  });
  const { recentSpeed, recentDraught } = useQueryVesselDetailMetrics({
    mmsi: id,
    ...dateRange,
  });

  const speeds = useMemo(
    () => ({
      data: filter(recentSpeed.data.data, ({ sog }) => isNumber(sog) && sog > 0).map(({ sog }) => sog ?? 0),
      categories: filter(recentSpeed.data.data, ({ sog }) => isNumber(sog) && sog > 0).map(
        ({ Date }) => dayjs(Date).format(FORMAT_DATE_TIME.DATE_MONTH) ?? '',
      ),
    }),
    [recentSpeed.data.data],
  );

  const draughts = useMemo(
    () => ({
      data: filter(recentDraught.data.data, ({ throughput }) => isNumber(throughput) && throughput > 0).map(
        ({ throughput }) => throughput ?? 0,
      ),
      categories: filter(recentDraught.data.data, ({ throughput }) => isNumber(throughput) && throughput > 0).map(
        ({ Date }) => dayjs(Date).format(FORMAT_DATE_TIME.DATE_MONTH) ?? '',
      ),
    }),
    [recentDraught.data.data],
  );

  const handleChangeDateRange = (e: DateRange<any>) => {
    const fromDate = handleDatePickerValueChange(FORMAT_DATE_TIME.yyyy_mm_dd, e[0]);
    const toDate = handleDatePickerValueChange(FORMAT_DATE_TIME.yyyy_mm_dd, e[1]);
    setDateRange({
      from_date: fromDate,
      to_date: toDate,
    });
  };

  return (
    <>
      <Stack direction={'row'} spacing={2}>
        <DateRangePickerWithDefaultIcon
          value={[
            getDatePickerValue(FORMAT_DATE_TIME.yyyy_mm_dd, dateRange.from_date),
            getDatePickerValue(FORMAT_DATE_TIME.yyyy_mm_dd, dateRange.to_date),
          ]}
          onChange={handleChangeDateRange}
          localeText={{
            start: 'From',
            end: 'To',
          }}
          slotProps={{
            textField: {
              size: 'small',
            },
          }}
          sx={{ maxWidth: '400px' }}
        />
      </Stack>

      <Stack mt={2} direction={'row'} spacing={3}>
        <VesselDetailBox p={2} flex={1}>
          <Typography mb={2} variant="h6">
            Recent speed (km/h)
          </Typography>
          <LineChart
            loading={recentSpeed.isFetching}
            tooltipFormatter={function () {
              const point: Point = this.point;
              return get(point, 'category').toString();
            }}
            categories={speeds.categories}
            height={300}
            data={speeds.data}
          />
        </VesselDetailBox>
        <VesselDetailBox p={2} flex={1}>
          <Typography mb={2} variant="h6">
            Recent draught (m)
          </Typography>
          <LineChart
            loading={recentDraught.isFetching}
            tooltipFormatter={function () {
              const point: Point = this.point;
              return get(point, 'category').toString();
            }}
            categories={draughts.categories}
            height={300}
            data={draughts.data}
          />
        </VesselDetailBox>
      </Stack>
    </>
  );
};

export default VesselMetrics;
